/** Intrusion configuration editor backed by the dashboard unit contract. */
import {
  Box,
  Button,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Form, Formik, getIn, setIn } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { patchSiteIntrusionConfig } from '@sophocles/api/odlApp';
import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import { intrusionConfigRequiresCommandLease } from '@sophocles/features/entities/site';
import { addToast } from '@sophocles/features/toast';
import useSiteCommandWrite from '@sophocles/hooks/useSiteCommandWrite';
import { applyApiErrorToFormik } from '@sophocles/utils/apiErrors';
import { SettingsFieldRow, SettingsSubsection, SettingsTextField } from '@sophocles/views/settings/components';
import DashboardBlade from '@sophocles/views/sites/components/SiteDashboard/DashboardBlade';
import {
  INTRUSION_RELAY_ACTION,
  INTRUSION_SCHEMA_VERSION,
  buildIntrusionInitialValues,
  buildIntrusionPatchKnownFields,
  buildIntrusionValidationSchema,
  generateIntrusionConfigPatch,
  hasIntrusionConfigChanges,
  mapIntrusionPatchErrorPath,
  resolveIntrusionBladeSections,
} from '@sophocles/views/sites/components/SiteDashboard/intrusionConfigForm';

const BLADE_WIDTH = { xs: '100%', sm: 640 };
const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value ?? {}, key);

const bladeContentSx = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  bgcolor: 'var(--surface-page)',
};

const bladeBodySx = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  bgcolor: 'var(--surface-page)',
};

function isEditable(section, field, canEdit) {
  return Boolean(canEdit && section?.editable_fields?.includes(field));
}

function errorAt(errors, touched, path) {
  return getIn(touched, path) ? getIn(errors, path) : undefined;
}

function ConfigNumberField({ name, label, disabled, required }) {
  return (
    <SettingsTextField
      name={name}
      type="number"
      disabled={disabled}
      required={required}
      hiddenLabel
      inputProps={{ min: 0, max: 255, step: 1, 'aria-label': label, 'aria-required': required || undefined }}
      size="small"
      fullWidth
    />
  );
}

ConfigNumberField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
};

function RelayActionRows({ namePrefix, relayActions, disabled, errors, touched, setFieldValue }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const relayNumbers = Object.keys(relayActions ?? {}).sort((left, right) => Number(left) - Number(right));

  if (relayNumbers.length === 0) return null;

  return (
    <>
      {relayNumbers.map(relayNumber => {
        const fieldName = `${namePrefix}.${relayNumber}`;
        const error = errorAt(errors, touched, fieldName);
        const errorId = `intrusion-${fieldName.replace(/[^a-zA-Z0-9_-]/g, '-')}-error`;
        const label = t('intrusion.relayActionForNumber', 'Action for relay {{number}}', {
          number: relayNumber,
        });
        return (
          <SettingsFieldRow key={relayNumber} label={label} controlWidth={280} stacked={isMobile}>
            <Box sx={{ width: '100%', maxWidth: { xs: 'none', sm: 280 }, ml: { sm: 'auto' } }}>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                disabled={disabled}
                value={Number(relayActions[relayNumber])}
                onChange={(_event, action) => {
                  if (action != null) setFieldValue(fieldName, action);
                }}
                aria-label={label}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? errorId : undefined}
              >
                <ToggleButton value={INTRUSION_RELAY_ACTION.NONE}>{t('general.none', 'None')}</ToggleButton>
                <ToggleButton value={INTRUSION_RELAY_ACTION.ON}>{t('general.on', 'On')}</ToggleButton>
                <ToggleButton value={INTRUSION_RELAY_ACTION.OFF}>{t('general.off', 'Off')}</ToggleButton>
              </ToggleButtonGroup>
              {error ? (
                <Typography
                  id={errorId}
                  variant="caption"
                  sx={{ display: 'block', color: 'error.main', mt: 'var(--space-1)' }}
                >
                  {error}
                </Typography>
              ) : null}
            </Box>
          </SettingsFieldRow>
        );
      })}
    </>
  );
}

RelayActionRows.propTypes = {
  namePrefix: PropTypes.string.isRequired,
  relayActions: PropTypes.object,
  disabled: PropTypes.bool,
  errors: PropTypes.object,
  touched: PropTypes.object,
  setFieldValue: PropTypes.func.isRequired,
};

function SettingsFields({ contract, values, permissions, errors, touched, setFieldValue }) {
  const { t } = useTranslation();
  const fields = contract ?? {};
  const hasFields = [
    'delay_before_relays_power_off_seconds',
    'automatic_rearm_time_minutes',
    'automatic_alarm_cancel_time_minutes',
    'relay_actions',
  ].some(field => hasOwn(fields, field));

  if (!hasFields) return null;

  const canEdit = permissions?.can_edit_settings ?? false;
  const actionDelayLabel = t('intrusion.actionDelaySeconds', 'Action Delay (seconds)');
  const rearmDelayLabel = t('intrusion.rearmDelayMinutes', 'Rearm Delay (minutes)');
  const alarmCancelDelayLabel = t('intrusion.alarmCancelDelayMinutes', 'Alarm Cancel Delay (minutes)');

  return (
    <SettingsSubsection
      title={t('intrusion.generalSettings', 'General settings')}
      description={t(
        'intrusion.generalSettingsDescription',
        'Timing and relay behavior shared by the intrusion inputs.',
      )}
      bordered
    >
      {hasOwn(fields, 'delay_before_relays_power_off_seconds') ? (
        <SettingsFieldRow
          label={actionDelayLabel}
          description={t(
            'intrusion.alarmDelayDescription',
            'How long to wait after the alarm is triggered before disabling the selected relays.',
          )}
          controlWidth={180}
          required={isEditable(fields, 'delay_before_relays_power_off_seconds', canEdit)}
        >
          <ConfigNumberField
            name="settings.delay_before_relays_power_off_seconds"
            label={actionDelayLabel}
            disabled={!isEditable(fields, 'delay_before_relays_power_off_seconds', canEdit)}
            required={isEditable(fields, 'delay_before_relays_power_off_seconds', canEdit)}
          />
        </SettingsFieldRow>
      ) : null}
      {hasOwn(fields, 'automatic_rearm_time_minutes') ? (
        <SettingsFieldRow
          label={rearmDelayLabel}
          description={t(
            'intrusion.rearmDelayDescription',
            'How long to wait after the sensor is disarmed before automatically rearming.',
          )}
          controlWidth={180}
          required={isEditable(fields, 'automatic_rearm_time_minutes', canEdit)}
        >
          <ConfigNumberField
            name="settings.automatic_rearm_time_minutes"
            label={rearmDelayLabel}
            disabled={!isEditable(fields, 'automatic_rearm_time_minutes', canEdit)}
            required={isEditable(fields, 'automatic_rearm_time_minutes', canEdit)}
          />
        </SettingsFieldRow>
      ) : null}
      {hasOwn(fields, 'automatic_alarm_cancel_time_minutes') ? (
        <SettingsFieldRow
          label={alarmCancelDelayLabel}
          controlWidth={180}
          required={isEditable(fields, 'automatic_alarm_cancel_time_minutes', canEdit)}
        >
          <ConfigNumberField
            name="settings.automatic_alarm_cancel_time_minutes"
            label={alarmCancelDelayLabel}
            disabled={!isEditable(fields, 'automatic_alarm_cancel_time_minutes', canEdit)}
            required={isEditable(fields, 'automatic_alarm_cancel_time_minutes', canEdit)}
          />
        </SettingsFieldRow>
      ) : null}

      {hasOwn(fields, 'relay_actions') ? (
        <RelayActionRows
          namePrefix="settings.relay_actions"
          relayActions={values.relay_actions}
          disabled={!isEditable(fields, 'relay_actions', canEdit)}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValue}
        />
      ) : null}
    </SettingsSubsection>
  );
}

SettingsFields.propTypes = {
  contract: PropTypes.object,
  values: PropTypes.object.isRequired,
  permissions: PropTypes.object,
  errors: PropTypes.object,
  touched: PropTypes.object,
  setFieldValue: PropTypes.func.isRequired,
};

function InputFields({ index, contract, values, permissions, errors, touched, setFieldValue }) {
  const { t } = useTranslation();
  const canEdit = permissions?.can_edit_inputs ?? false;
  const prefix = `inputs[${index}]`;
  const polarityError = errorAt(errors, touched, `${prefix}.polarity`);
  const nameLabel = t('intrusion.name', 'Name');
  const enabledLabel = t('intrusion.enabled', 'Enabled');
  const polarityLabel = t('intrusion.polarity', 'Polarity');
  const actionDelayLabel = t('intrusion.actionDelaySeconds', 'Action Delay (seconds)');
  const reactivationDelayLabel = t('intrusion.reactivationDelayMinutes', 'Reactivation Delay (minutes)');

  return (
    <SettingsSubsection
      title={t('intrusion.inputWithNumber', 'Input {{number}}', { number: values.input_number })}
      bordered
    >
      {hasOwn(contract, 'name') ? (
        <SettingsFieldRow label={nameLabel} controlWidth={320} required={isEditable(contract, 'name', canEdit)}>
          <SettingsTextField
            name={`${prefix}.name`}
            disabled={!isEditable(contract, 'name', canEdit)}
            required={isEditable(contract, 'name', canEdit)}
            hiddenLabel
            inputProps={{
              maxLength: 50,
              'aria-label': nameLabel,
              'aria-required': isEditable(contract, 'name', canEdit) || undefined,
            }}
            size="small"
            fullWidth
          />
        </SettingsFieldRow>
      ) : null}
      {hasOwn(contract, 'enabled') ? (
        <SettingsFieldRow label={enabledLabel} controlWidth={80}>
          <Switch
            checked={Boolean(values.enabled)}
            disabled={!isEditable(contract, 'enabled', canEdit)}
            onChange={event => setFieldValue(`${prefix}.enabled`, event.target.checked)}
            inputProps={{ 'aria-label': enabledLabel }}
          />
        </SettingsFieldRow>
      ) : null}
      {hasOwn(contract, 'polarity') ? (
        <SettingsFieldRow label={polarityLabel} controlWidth={200}>
          <TextField
            name={`${prefix}.polarity`}
            select
            value={String(Boolean(values.polarity))}
            disabled={!isEditable(contract, 'polarity', canEdit)}
            onChange={event => setFieldValue(`${prefix}.polarity`, event.target.value === 'true')}
            error={Boolean(polarityError)}
            helperText={polarityError}
            hiddenLabel
            variant="filled"
            inputProps={{ 'aria-label': polarityLabel }}
            size="small"
            fullWidth
          >
            <MenuItem value="false">{t('intrusion.polarityNO', 'Normally open')}</MenuItem>
            <MenuItem value="true">{t('intrusion.polarityNC', 'Normally closed')}</MenuItem>
          </TextField>
        </SettingsFieldRow>
      ) : null}
      {hasOwn(contract, 'alarm_delay_seconds') ? (
        <SettingsFieldRow
          label={actionDelayLabel}
          description={t(
            'intrusion.actionDelayDescription',
            'How long to wait after the sensor is tripped before performing relay actions and sending alarms.',
          )}
          controlWidth={180}
          required={isEditable(contract, 'alarm_delay_seconds', canEdit)}
        >
          <ConfigNumberField
            name={`${prefix}.alarm_delay_seconds`}
            label={actionDelayLabel}
            disabled={!isEditable(contract, 'alarm_delay_seconds', canEdit)}
            required={isEditable(contract, 'alarm_delay_seconds', canEdit)}
          />
        </SettingsFieldRow>
      ) : null}
      {hasOwn(contract, 'reactivation_delay_minutes') ? (
        <SettingsFieldRow
          label={reactivationDelayLabel}
          description={t(
            'intrusion.reactivationDelayDescriptionV2',
            'How long to wait after the sensor is tripped before returning relays to their scheduled state.',
          )}
          controlWidth={180}
          required={isEditable(contract, 'reactivation_delay_minutes', canEdit)}
        >
          <ConfigNumberField
            name={`${prefix}.reactivation_delay_minutes`}
            label={reactivationDelayLabel}
            disabled={!isEditable(contract, 'reactivation_delay_minutes', canEdit)}
            required={isEditable(contract, 'reactivation_delay_minutes', canEdit)}
          />
        </SettingsFieldRow>
      ) : null}

      {hasOwn(contract, 'relay_actions') ? (
        <RelayActionRows
          namePrefix={`${prefix}.relay_actions`}
          relayActions={values.relay_actions}
          disabled={!isEditable(contract, 'relay_actions', canEdit)}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValue}
        />
      ) : null}
    </SettingsSubsection>
  );
}

InputFields.propTypes = {
  index: PropTypes.number.isRequired,
  contract: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  permissions: PropTypes.object,
  errors: PropTypes.object,
  touched: PropTypes.object,
  setFieldValue: PropTypes.func.isRequired,
};

function IntrusionBladeSkeleton() {
  return (
    <Stack sx={{ gap: 'var(--space-3)' }}>
      <Skeleton variant="text" width="40%" />
      {[0, 1, 2, 3].map(index => (
        <Skeleton key={index} variant="rounded" height={40} />
      ))}
    </Stack>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {number|string} props.siteId
 * @param {object|null} props.intrusionConfiguration
 * @param {{can_edit_settings?: boolean, can_edit_inputs?: boolean}} props.permissions
 * @param {boolean} [props.loading=false]
 */
export default function IntrusionBlade({
  open,
  onClose,
  siteId,
  intrusionConfiguration,
  permissions,
  loading: dashboardLoading = false,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { commandLocked, runWrite } = useSiteCommandWrite(siteId);
  const [configSnapshot, setConfigSnapshot] = useState(null);
  const [nonFieldMessages, setNonFieldMessages] = useState([]);

  const config = configSnapshot?.siteId === siteId ? configSnapshot.configuration : null;
  const snapshotPermissions = configSnapshot?.siteId === siteId ? configSnapshot.permissions : {};
  const hasSupportedIncomingConfig = intrusionConfiguration?.schema_version === INTRUSION_SCHEMA_VERSION;
  const loading = open && config == null && (dashboardLoading || hasSupportedIncomingConfig);
  const loadError = open && config == null && !loading;

  useEffect(() => {
    if (!open) {
      setConfigSnapshot(null);
      setNonFieldMessages([]);
      return undefined;
    }
    if (config == null && hasSupportedIncomingConfig) {
      setConfigSnapshot({
        siteId,
        configuration: intrusionConfiguration,
        permissions: { ...permissions },
      });
    }
    return undefined;
  }, [config, hasSupportedIncomingConfig, intrusionConfiguration, open, permissions, siteId]);

  useEffect(() => {
    if (open) setNonFieldMessages([]);
  }, [open, siteId]);

  const validationMessages = useMemo(
    () => ({
      required: t('validation.required', 'Required'),
      number: t('validation.number', 'Enter a number'),
      wholeNumber: t('validation.wholeNumber', 'Enter a whole number'),
      range: t('intrusion.validationBetween0and255', 'Value must be an integer between 0 and 255'),
      nameLength: t('intrusion.validationNameMaxLength', 'Name must be 50 characters or fewer'),
      relayAction: t('intrusion.validationRelayAction', 'Select a valid relay action'),
      actionDelayLessThanReactivation: t(
        'intrusion.validationActionDelayLessThanReactivation',
        'Action delay must be shorter than reactivation delay',
      ),
    }),
    [t],
  );

  const initialValues = useMemo(() => buildIntrusionInitialValues(config), [config]);
  const validationSchema = useMemo(
    () => (config ? buildIntrusionValidationSchema(config, snapshotPermissions, validationMessages) : undefined),
    [config, snapshotPermissions, validationMessages],
  );
  const handleSubmit = async (values, { setErrors, setSubmitting }) => {
    setNonFieldMessages([]);
    if (!config) {
      setSubmitting(false);
      return;
    }
    const patch = generateIntrusionConfigPatch(config, values, snapshotPermissions);
    if (!hasIntrusionConfigChanges(patch)) {
      setSubmitting(false);
      return;
    }
    const requiresCommand = intrusionConfigRequiresCommandLease(patch);
    try {
      const outcome = await runWrite(() => patchSiteIntrusionConfig(siteId, patch), { requiresCommand });
      if (!outcome.executed) return;
      const saved = outcome.value;
      if (saved?.schema_version !== INTRUSION_SCHEMA_VERSION) {
        throw new Error('Unsupported intrusion configuration response');
      }
      dispatch(
        addToast({ message: t('intrusion.settingsUpdated', 'Intrusion Sensor Settings Updated'), level: 'success' }),
      );
      onClose();
    } catch (error) {
      setNonFieldMessages(
        applyApiErrorToFormik(error, {
          setErrors: fieldErrors => {
            const nestedErrors = Object.entries(fieldErrors).reduce(
              (result, [path, message]) => setIn(result, mapIntrusionPatchErrorPath(path, patch, values), message),
              {},
            );
            setErrors(nestedErrors);
          },
          knownFields: buildIntrusionPatchKnownFields(patch),
          genericMessage: t('intrusion.configurationUpdateFailed', 'Intrusion configuration could not be saved.'),
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, dirty, isValid, isSubmitting, setFieldValue, submitForm }) => {
        const sections = resolveIntrusionBladeSections(config, values);
        const hasSections = Boolean(sections.settings || sections.inputs.length > 0);
        const pendingPatch = config ? generateIntrusionConfigPatch(config, values, snapshotPermissions) : null;
        const saveRequiresCommand = intrusionConfigRequiresCommandLease(pendingPatch);
        const canSave = Boolean(
          config &&
            dirty &&
            isValid &&
            !isSubmitting &&
            !(commandLocked && saveRequiresCommand) &&
            hasIntrusionConfigChanges(pendingPatch),
        );
        const saveAction = (
          <Button variant="contained" size="small" disabled={!canSave} onClick={submitForm} sx={{ flexShrink: 0 }}>
            {t('actions.save', 'Save')}
          </Button>
        );

        return (
          <Form>
            <DashboardBlade
              open={open}
              onClose={onClose}
              width={BLADE_WIDTH}
              title={t('sites.intrusionSettings', 'Intrusion Settings')}
              headerAction={saveAction}
            >
              <Box sx={bladeContentSx}>
                <FormErrorAlert messages={nonFieldMessages} sx={{ mx: 'var(--space-3)', mt: 'var(--space-3)' }} />

                {!open ? null : loading ? (
                  <Box sx={{ ...bladeBodySx, p: 'var(--space-3)' }}>
                    <IntrusionBladeSkeleton />
                  </Box>
                ) : loadError ? (
                  <Box sx={{ ...bladeBodySx, p: 'var(--space-3)' }}>
                    <Typography color="error">
                      {intrusionConfiguration != null && !hasSupportedIncomingConfig
                        ? t(
                            'intrusion.unsupportedConfiguration',
                            'This intrusion configuration format is not supported.',
                          )
                        : t('intrusion.configurationLoadFailed', 'Intrusion configuration could not be loaded.')}
                    </Typography>
                  </Box>
                ) : !hasSections ? (
                  <Box sx={{ ...bladeBodySx, p: 'var(--space-3)' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('intrusion.noInputs', 'No intrusion inputs were reported.')}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ ...bladeBodySx, p: 'var(--space-3)' }}>
                    <Stack spacing={0} sx={{ '& > :first-child': { pt: 0 } }}>
                      {sections.settings ? (
                        <SettingsFields
                          contract={sections.settings.contract}
                          values={sections.settings.values}
                          permissions={snapshotPermissions}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                        />
                      ) : null}

                      {sections.inputs.map(section => (
                        <InputFields
                          key={section.key}
                          index={section.inputIndex}
                          contract={section.contract}
                          values={section.values}
                          permissions={snapshotPermissions}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            </DashboardBlade>
          </Form>
        );
      }}
    </Formik>
  );
}

IntrusionBlade.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  intrusionConfiguration: PropTypes.object,
  permissions: PropTypes.shape({
    can_edit_settings: PropTypes.bool,
    can_edit_inputs: PropTypes.bool,
  }).isRequired,
  loading: PropTypes.bool,
};
